import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAccount } from "@azure/msal-react";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GavelIcon from "@mui/icons-material/Gavel";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGetMyProfileQuery } from "../../../services/ProfileService";

interface QuickActionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
  color: "primary" | "secondary" | "success" | "warning" | "info" | "error";
}

function QuickActionCard({
  icon,
  title,
  description,
  path,
  color,
}: QuickActionProps) {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-4px)",
          borderColor: `${color}.main`,
        },
        cursor: "pointer",
      }}
      onClick={() => navigate(path)}
    >
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ color: `${color}.main`, mb: 2, fontSize: 32 }}>
          {icon}
        </Box>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mb: 1 }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, flexGrow: 1 }}
        >
          {description}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", color: `${color}.main` }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Open
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function LoggedInDashboard() {
  const account = useAccount();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoading: isProfileLoading } = useGetMyProfileQuery();

  const firstName =
    account?.name?.split(" ")[0] || "User";

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        {/* Welcome Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            {isProfileLoading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={28} />
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "primary.dark",
                  }}
                >
                  Loading your workspace...
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "primary.dark",
                }}
              >
                Welcome back, {firstName}! 👋
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              Select a feature from the sidebar menu to get started managing your healthcare operations.
            </Typography>
          </Stack>
        </Paper>

      </Container>
    </Box>
  );
}

export default LoggedInDashboard;
